import { signUp, signIn, signOutUser, resetPassword, onAuthStateChange } from './auth.js';
import { auth } from './firebase-config.js';
import { subscriptionManager } from './subscription.js';
import { trackSignUp, trackCheckoutClick, trackClick } from './analytics.js';
import { getCheckoutUrl } from './checkout-urls.js';

// Create auth modal UI elements
export const createAuthUI = () => {
    const authContainer = document.createElement('div');
    authContainer.className = 'auth-container';
    authContainer.style.display = 'none'; // Hidden by default
    authContainer.innerHTML = `
        <div class="auth-box">
            <div class="auth-header">
                <button class="close-auth-modal" id="close-auth-modal">
                    <i class="fa fa-times"></i>
                </button>
            </div>
            <div class="mandatory-notice" style="display: none;">
                <p><i class="fa fa-lock"></i> Sign in required to continue</p>
            </div>
            <div id="auth-forms">
                <!-- Sign In Form -->
                <form id="signin-form" class="auth-form">
                    <h2>Please Sign in</h2>
                    <input type="email" id="signin-email" placeholder="Email" required>
                    <input type="password" id="signin-password" placeholder="Password" required>
                    <button type="submit">Sign In</button>
                    <p><strong>New Premium User? </strong> or Don't have an account? <br><a href="#" id="show-signup">Sign Up</a></p>
               
                    <p><a href="#" id="show-reset">Forgot Password?</a></p>
                </form>

                <!-- Sign Up Form -->
                <form id="signup-form" class="auth-form" style="display: none;">
                    <h2>Sign Up</h2>
                    <input type="text" id="signup-name" placeholder="Name (optional)">
                    <input type="email" id="signup-email" placeholder="Email" required>
                    <input type="password" id="signup-password" placeholder="Password" required>
                    <input type="password" id="signup-confirm-password" placeholder="Confirm Password" required>
                    <button type="submit">Sign Up</button>
                    <p>Already have an account? <a href="#" id="show-signin">Sign In</a></p>
                </form>

                <!-- Reset Password Form -->
                <form id="reset-form" class="auth-form" style="display: none;">
                    <h2>Reset Password</h2>
                    <input type="email" id="reset-email" placeholder="Email" required>
                    <p class="reset-note">If you don't see the email in your inbox, check your spam or junk folder.</p>
                    <button type="submit">Reset Password</button>
                    <p><a href="#" id="back-to-signin">Back to Sign In</a></p>
                </form>
            </div>
        </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
        .auth-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 9999;
        }

        .auth-container[data-mandatory="true"] {
            background: rgba(0, 0, 0, 0.8);
            z-index: 10000;
        }

        .auth-box {
            background: white;
            padding: 2rem;
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
            width: 100%;
            max-width: 400px;
            position: relative;
        }

        .auth-container[data-mandatory="true"] .auth-box {
            box-shadow: 0 12px 48px rgba(0, 0, 0, 0.3);
            border: 2px solid #4CAF50;
        }

        .auth-header {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 1rem;
        }

        .close-auth-modal {
            background: none;
            border: none;
            font-size: 20px;
            color: #666;
            cursor: pointer;
            padding: 5px;
            border-radius: 50%;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
        }

        .auth-container[data-mandatory="true"] .close-auth-modal {
            display: none;
        }

        .close-auth-modal:hover {
            background: #f0f0f0;
            color: #333;
        }

        .auth-form {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        .auth-form h2 {
            text-align: center;
            color: #333;
            margin-bottom: 1.5rem;
        }

        .auth-form input {
            padding: 0.75rem;
            border: 2px solid #ddd;
            border-radius: 8px;
            font-size: 14px;
            transition: border-color 0.2s ease;
        }

        .auth-form input:focus {
            outline: none;
            border-color: #4CAF50;
        }

        .auth-form button {
            padding: 0.75rem;
            background: linear-gradient(135deg, #4CAF50, #45a049);
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            font-size: 16px;
            font-weight: 600;
            transition: all 0.3s ease;
            box-shadow: 0 2px 6px rgba(76, 175, 80, 0.3);
        }

        .auth-form button:hover {
            background: linear-gradient(135deg, #45a049, #3d8b40);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
        }

        .auth-form p {
            text-align: center;
            margin: 0.5rem 0;
            color: #666;
        }

        .auth-form .reset-note {
            font-size: 0.9rem;
            margin: 0.25rem 0 0.75rem;
        }

        .auth-form a {
            color: #4CAF50;
            text-decoration: none;
            font-weight: 600;
        }

        .auth-form a:hover {
            text-decoration: underline;
        }

        .mandatory-notice {
            background: linear-gradient(135deg, #ff6b6b, #ee5a52);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            text-align: center;
            margin-bottom: 1rem;
            font-size: 13px;
            font-weight: 600;
            box-shadow: 0 2px 6px rgba(255, 107, 107, 0.3);
        }

        .auth-container[data-mandatory="true"] .mandatory-notice {
            display: block;
        }

        .mandatory-notice p {
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
        }

        .auth-error {
            background: #ffebee;
            color: #c62828;
            padding: 10px;
            border-radius: 6px;
            margin-bottom: 1rem;
            font-size: 14px;
            display: none;
            animation: fadeIn 0.3s ease;
        }

        .auth-success {
            background: #e8f5e9;
            color: #2e7d32;
            padding: 10px;
            border-radius: 6px;
            margin-bottom: 1rem;
            font-size: 14px;
            display: none;
            animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
        }
    `;
    document.head.appendChild(style);

    // Add event listeners
    const signinForm = authContainer.querySelector('#signin-form');
    const signupForm = authContainer.querySelector('#signup-form');
    const resetForm = authContainer.querySelector('#reset-form');
    const closeModalBtn = authContainer.querySelector('#close-auth-modal');

    // Form navigation
    authContainer.querySelector('#show-signup').addEventListener('click', (e) => {
        e.preventDefault();
        signinForm.style.display = 'none';
        signupForm.style.display = 'flex';
        resetForm.style.display = 'none';
    });

    authContainer.querySelector('#show-signin').addEventListener('click', (e) => {
        e.preventDefault();
        signinForm.style.display = 'flex';
        signupForm.style.display = 'none';
        resetForm.style.display = 'none';
    });

    authContainer.querySelector('#show-reset').addEventListener('click', (e) => {
        e.preventDefault();
        signinForm.style.display = 'none';
        signupForm.style.display = 'none';
        resetForm.style.display = 'flex';
    });

    authContainer.querySelector('#back-to-signin').addEventListener('click', (e) => {
        e.preventDefault();
        signinForm.style.display = 'flex';
        signupForm.style.display = 'none';
        resetForm.style.display = 'none';
    });

    // Form submissions
    signinForm.addEventListener('submit', handleSignIn);
    signupForm.addEventListener('submit', handleSignUp);
    resetForm.addEventListener('submit', handleReset);

    // Close modal functionality
    closeModalBtn.addEventListener('click', () => {
        // Only allow closing if not mandatory
        if (!authContainer.hasAttribute('data-mandatory')) {
            authContainer.style.display = 'none';
        }
    });

    // Close modal when clicking outside
    authContainer.addEventListener('click', (e) => {
        // Only allow closing if not mandatory and clicking on background
        if (e.target === authContainer && !authContainer.hasAttribute('data-mandatory')) {
            authContainer.style.display = 'none';
        }
    });

    // Prevent closing with Escape key when mandatory
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && authContainer.style.display === 'flex' && authContainer.hasAttribute('data-mandatory')) {
            e.preventDefault();
            e.stopPropagation();
        }
    });

    return authContainer;
};

// Initialize navigation auth integration
export const initNavAuth = () => {
    const loginPrompt = document.getElementById('login-prompt');
    const userAvatar = document.getElementById('user-avatar');
    const navUsername = document.getElementById('nav-username');
    const showAuthModalBtn = document.getElementById('show-auth-modal');
    const getStartedFreeBtn = document.getElementById('get-started-free-btn');
    const navSignoutBtn = document.getElementById('nav-signout');
    const pricingNavBtn = document.getElementById('pricing-nav-btn');

    // Show/hide Pricing and mobile overlay buttons based on subscription
    // Pricing/Homepage: only visible to non-premium users
    const setPremiumNavVisibility = (subscriptionState) => {
        const showUpgrade = !subscriptionState.isPremium ? 'flex' : 'none';
        if (pricingNavBtn) pricingNavBtn.style.display = showUpgrade;
        const mobileHomepageBtn = document.getElementById('mobile-overlay-homepage-btn');
        if (mobileHomepageBtn) mobileHomepageBtn.style.display = subscriptionState.isPremium ? 'none' : 'flex';
        const mobileJournalBtn = document.getElementById('mobile-overlay-journal-btn');
        if (mobileJournalBtn) mobileJournalBtn.style.display = 'flex';
    };
    if (pricingNavBtn || document.getElementById('mobile-overlay-homepage-btn') || document.getElementById('mobile-overlay-journal-btn')) {
        subscriptionManager.onSubscriptionChange(setPremiumNavVisibility);
    }
    // Always reveal nav once we have subscription state for logged-in users (in case nav buttons are absent)
    subscriptionManager.onSubscriptionChange((state) => {
        if (state.isLoggedIn) document.body.classList.add('auth-ready');
    });

    // Create and append auth modal
    const authModal = createAuthUI();
    document.body.appendChild(authModal);
    
    /** Use Firebase session (not subscription cache) so ?intent=signup is not misread during auth restore. */
    function isFirebaseSignedIn() {
        return !!(auth && auth.currentUser);
    }

    // Legacy ?intent=upgrade links: send everyone straight to Lemon Squeezy (no auth required)
    function handleUpgradeIntentIfPresent() {
        const urlParams = new URLSearchParams(window.location.search);
        const intent = urlParams.get('intent');
        if (intent !== 'upgrade') return;
        const productParam = urlParams.get('product');
        const checkoutUrl = getCheckoutUrl(productParam || null, auth.currentUser);
        const cleanUrl = (window.location.pathname || '/') + (window.location.hash || '');
        window.history.replaceState({}, '', cleanUrl); // clear intent from URL so we only handle once
        trackCheckoutClick(productParam || 'annual', 'intent_upgrade_redirect');
        window.location.replace(checkoutUrl);
    }

    // Landing "Get Started Free" (?intent=signup): open sign-up modal on main app; URL is cleaned after handling
    function handleSignupIntentIfPresent() {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('intent') !== 'signup') return;
        const cleanUrl = (window.location.pathname || '/') + (window.location.hash || '');
        window.history.replaceState({}, '', cleanUrl);
        if (isFirebaseSignedIn()) return;
        authModal.style.display = 'flex';
        setAuthModalFormView('signup');
        updateSignUpButtonForContext();
        trackClick('landing_intent_signup_modal');
    }

    /**
     * Run ?intent= handlers only after initial auth is settled, then defer one task so:
     * - onAuthStateChange has run and updated the nav (and auth.currentUser matches the first stable state)
     * - signup modal is not immediately hidden by a follow-up auth tick in the same turn
     */
    function runLandingIntentAfterAuthReady() {
        const run = () => {
            const intent = new URLSearchParams(window.location.search).get('intent');
            if (intent === 'upgrade') {
                handleUpgradeIntentIfPresent();
            } else if (intent === 'signup') {
                handleSignupIntentIfPresent();
            }
        };
        const schedule = () => {
            setTimeout(run, 0);
        };
        if (auth && typeof auth.authStateReady === 'function') {
            auth.authStateReady().then(schedule).catch(schedule);
        } else {
            schedule();
        }
    }

    function setAuthModalFormView(view) {
        const signinFormEl = authModal.querySelector('#signin-form');
        const signupFormEl = authModal.querySelector('#signup-form');
        const resetFormEl = authModal.querySelector('#reset-form');
        if (view === 'signup') {
            if (signinFormEl) signinFormEl.style.display = 'none';
            if (signupFormEl) signupFormEl.style.display = 'flex';
            if (resetFormEl) resetFormEl.style.display = 'none';
        } else {
            // sign-in (default for Login button)
            if (signinFormEl) signinFormEl.style.display = 'flex';
            if (signupFormEl) signupFormEl.style.display = 'none';
            if (resetFormEl) resetFormEl.style.display = 'none';
        }
    }

    // Show auth modal when login button is clicked — always show sign-in form
    showAuthModalBtn.addEventListener('click', () => {
        trackClick('open_auth_modal');
        authModal.style.display = 'flex';
        setAuthModalFormView('signin');
        updateSignUpButtonForContext();
    });

    // Get Started Free: open modal on sign-up form
    if (getStartedFreeBtn) {
        getStartedFreeBtn.addEventListener('click', () => {
            trackClick('open_auth_modal_get_started');
            authModal.style.display = 'flex';
            setAuthModalFormView('signup');
            updateSignUpButtonForContext();
        });
    }

    // Function to update sign-up button text based on context
    function updateSignUpButtonForContext() {
        const signupButton = authModal.querySelector('#signup-form button[type="submit"]');
        if (signupButton) {
            signupButton.textContent = 'Sign Up';
        }
    }
    
    // Also update button text when user switches to sign-up form
    authModal.querySelector('#show-signup').addEventListener('click', (e) => {
        setTimeout(() => updateSignUpButtonForContext(), 0);
    });
    
    // Handle signout from navigation
    navSignoutBtn.addEventListener('click', async () => {
        const { error } = await signOutUser();
        if (error) {
            alert(error);
        }
    });

    // Auth state observer for navigation — register before landing intents so the first stable auth tick runs first
    onAuthStateChange((user) => {
        if (user) {
            // User is logged in - show avatar, hide login button (auth-ready set when subscription state is known)
            loginPrompt.style.display = 'none';
            userAvatar.style.display = 'block';
            
            // Use display name if available, otherwise extract username from email
            let displayText;
            if (user.displayName && user.displayName.trim()) {
                displayText = user.displayName;
            } else {
                displayText = user.email.split('@')[0];
            }
            navUsername.textContent = displayText;
            
            // Hide auth modal if it's open
            authModal.style.display = 'none';
        } else {
            // User is logged out - show login button, hide avatar; reveal nav immediately
            // Use flex (not block) so #login-prompt keeps a horizontal row; inline display overrides CSS
            loginPrompt.style.display = 'flex';
            userAvatar.style.display = 'none';
            document.body.classList.add('auth-ready');
        }
    });

    runLandingIntentAfterAuthReady();
};

// Add error message display
const showError = (formId, message) => {
    const form = document.getElementById(formId);
    let errorDiv = form.querySelector('.auth-error');
    
    if (!errorDiv) {
        errorDiv = document.createElement('div');
        errorDiv.className = 'auth-error';
        form.insertBefore(errorDiv, form.firstChild);
    }
    
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
        errorDiv.style.display = 'none';
    }, 5000);
};

// Add success message display
const showSuccess = (formId, message) => {
    const form = document.getElementById(formId);
    let successDiv = form.querySelector('.auth-success');
    
    if (!successDiv) {
        successDiv = document.createElement('div');
        successDiv.className = 'auth-success';
        form.insertBefore(successDiv, form.firstChild);
    }
    
    successDiv.textContent = message;
    successDiv.style.display = 'block';
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
        successDiv.style.display = 'none';
    }, 5000);
};

// Add styles for error and success messages
const style = document.createElement('style');
style.textContent += `
    .auth-error {
        background: #ffebee;
        color: #c62828;
        padding: 10px;
        border-radius: 6px;
        margin-bottom: 1rem;
        font-size: 14px;
        display: none;
        animation: fadeIn 0.3s ease;
    }

    .auth-success {
        background: #e8f5e9;
        color: #2e7d32;
        padding: 10px;
        border-radius: 6px;
        margin-bottom: 1rem;
        font-size: 14px;
        display: none;
        animation: fadeIn 0.3s ease;
    }

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;

// Add hide modal function
export const hideAuthModal = () => {
    const authContainer = document.querySelector('.auth-container');
    if (authContainer) {
        authContainer.style.display = 'none';
        
        // Reset forms
        document.getElementById('signin-form').style.display = 'flex';
        document.getElementById('signup-form').style.display = 'none';
        document.getElementById('reset-form').style.display = 'none';
        
        // Clear inputs
        document.getElementById('signin-email').value = '';
        document.getElementById('signin-password').value = '';
        document.getElementById('signup-email').value = '';
        document.getElementById('signup-password').value = '';
        document.getElementById('signup-confirm-password').value = '';
        document.getElementById('signup-name').value = '';
        document.getElementById('reset-email').value = '';
        
        // Remove mandatory status if present
        authContainer.removeAttribute('data-mandatory');
        
        // Reset sign-up button text to default
        const signupButton = document.getElementById('signup-form')?.querySelector('button[type="submit"]');
        if (signupButton) {
            signupButton.textContent = 'Sign Up';
        }
    }
};

// Form submission handlers
const handleSignIn = async (e) => {
    e.preventDefault();
    const email = document.getElementById('signin-email').value;
    const password = document.getElementById('signin-password').value;
    
    const { user, error } = await signIn(email, password);
    
    if (error) {
        showError('signin-form', error);
    } else {
        showSuccess('signin-form', 'Successfully signed in!');
        setTimeout(() => hideAuthModal(), 1000);
    }
};

const handleSignUp = async (e) => {
    e.preventDefault();
    const name = document.getElementById('signup-name').value;
    const email = document.getElementById('signup-email').value;
    const password = document.getElementById('signup-password').value;
    const confirmPassword = document.getElementById('signup-confirm-password').value;
    
    // Check if passwords match
    if (password !== confirmPassword) {
        showError('signup-form', 'Passwords do not match. Please try again.');
        return;
    }
    
    const { user, error } = await signUp(email, password, name);
    
    if (error) {
        showError('signup-form', error);
    } else {
        trackSignUp('email');
        showSuccess('signup-form', 'Account created successfully!');
        setTimeout(() => {
            hideAuthModal();
        }, 1000);
    }
};

const handleReset = async (e) => {
    e.preventDefault();
    const email = document.getElementById('reset-email').value;
    
    const { error } = await resetPassword(email);
    
    if (error) {
        showError('reset-form', error);
    } else {
        showSuccess('reset-form', 'Password reset email sent! Please check your inbox (and spam folder if you don\'t see it).');
    }
}; 